import { useState } from "react";
import { 
  ExternalLink, 
  Download, 
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  FileDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TapTrackPanel = ({ 
  tapTrackSettings, 
  setTapTrackSettings,
  tapTrackHistory,
  onRefresh,
  token
}) => {
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.post(`${API}/taptrack/export`, {}, { headers });
      toast.success(`Exported ${response.data.exported_count} leads to TapTrack`);
      onRefresh();
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/taptrack/settings`, tapTrackSettings, { headers });
      toast.success("TapTrack settings saved");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  const statusColors = {
    success: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle },
    failed: { bg: "bg-red-100", text: "text-red-700", icon: XCircle },
    pending: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock }
  };

  return (
    <div className="p-6" data-testid="taptrack-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">TapTrack Export</h1>
          <p className="text-gray-600">Export leads to TapTrack Ops system</p>
        </div>
        <Button onClick={onRefresh} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Export Action */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" />
            Export Leads
          </h2>
          <p className="text-gray-500 mb-6">
            Export all new leads to TapTrack Ops for project management and scheduling.
          </p>
          
          <Button 
            onClick={handleExport} 
            disabled={exporting}
            className="w-full"
            size="lg"
          >
            {exporting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                Export to TapTrack
              </>
            )}
          </Button>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Only leads marked as "ready for export" will be sent. 
              Leads already exported will be skipped.
            </p>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-primary" />
            TapTrack Settings
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label>TapTrack API Endpoint</Label>
              <Input
                value={tapTrackSettings.api_endpoint || ""}
                onChange={(e) => setTapTrackSettings(prev => ({ ...prev, api_endpoint: e.target.value }))}
                placeholder="https://api.taptrack.com/v1"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>API Key</Label>
              <Input
                type="password"
                value={tapTrackSettings.api_key || ""}
                onChange={(e) => setTapTrackSettings(prev => ({ ...prev, api_key: e.target.value }))}
                placeholder="Enter your TapTrack API key"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Business ID</Label>
              <Input
                value={tapTrackSettings.business_id || ""}
                onChange={(e) => setTapTrackSettings(prev => ({ ...prev, business_id: e.target.value }))}
                placeholder="Your TapTrack business ID"
                className="mt-1"
              />
            </div>
          </div>

          <Button 
            onClick={handleSaveSettings} 
            disabled={saving}
            className="w-full mt-6"
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>

      {/* Export History */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Export History</h2>
        </div>
        
        {tapTrackHistory.length > 0 ? (
          <div className="divide-y">
            {tapTrackHistory.map((record, idx) => {
              const status = statusColors[record.status] || statusColors.pending;
              const StatusIcon = status.icon;
              
              return (
                <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${status.bg}`}>
                      <StatusIcon className={`w-5 h-5 ${status.text}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {record.exported_count || 0} leads exported
                      </p>
                      <p className="text-sm text-gray-500">{formatDate(record.created_at)}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                    {record.status}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-400">
            <FileDown className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No export history yet</p>
            <p className="text-sm">Export leads to TapTrack to see history here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TapTrackPanel;
